import { Heightfield } from '../terrain/heightfield'
import { ObstacleField } from './obstacles'
import { blendSnow, makeSnowSample, SNOW_TYPE_COUNT } from '../terrain/snow'
import type { SnowSample } from '../terrain/snow'
import { Noise2D, mulberry32 } from '../core/rng'
import { TUNE } from './tuning'
import { RAD, clamp, clamp01, damp, lerp, smoothstep } from '../core/math'

/**
 * The skier solver. Spec §7: "Do not reach for a full rigid-body engine."
 *
 * A point mass with an orientation, constrained to the heightfield surface and
 * ballistic when it leaves. Purpose-built, allocation-free per step, and — note
 * the imports — completely free of Three.js, so Phase 4's server can re-simulate
 * a submitted input stream in Node without a renderer.
 */

export interface InputFrame {
  /** -1 (full left) .. +1 (full right). */
  steer: number
  /** Hold: tuck / straightline. */
  tuck: boolean
  /** Swipe down held: check speed / slarve. */
  brake: boolean
  /** Swipe up, edge-triggered: pop off a lip. */
  pop: boolean
}

export function emptyInput(): InputFrame {
  return { steer: 0, tuck: false, brake: false, pop: false }
}

export type RideState = 'ride' | 'air' | 'sketch' | 'crash'

/**
 * Why the last crash happened. Design pillar #4 is legible failure — "you
 * always know why the run scored what it scored" — and that starts with the
 * sim being able to say it in one word.
 */
export type CrashReason =
  | 'none'
  /** Lost the edge and slid out sideways. */
  | 'spinout'
  /** Came down too hard into the slope. */
  | 'flat landing'
  /** Landed pointing somewhere other than where you were going. */
  | 'sideways landing'
  /** Hit a tree or a rock. */
  | 'impact'
  /** Punched through breakable crust at speed and it grabbed a ski. */
  | 'broke through'
  /** Caught and buried by your own sluff. */
  | 'sluffed'

/** Branchable physics state. See Skier.save / Skier.load. */
export interface SkierSnapshot {
  x: number; y: number; z: number
  vx: number; vy: number; vz: number
  heading: number; edge: number
  airborne: boolean
  crashTimer: number; sketchTimer: number; popCooldown: number; airTime: number
  crashes: number; distance: number; verticalDropped: number
  sluffLoad: number; breakThroughTimer: number
  prevPop: boolean
  /**
   * Telemetry that feeds back into the simulation and therefore is *state*.
   * Sluff reads gripUsed to decide how much snow the skis are cutting loose, so
   * leaving it out let it leak between beam-search branches and made the
   * solver's own trajectory unreproducible.
   */
  gripUsed: number; skid: number
  /**
   * Assist state. All three are damped or timed, i.e. history-dependent, so
   * they are state by the same argument as gripUsed. tools/check-determinism.ts
   * exists to catch the day someone adds a fourth and forgets this block.
   */
  autoYaw: number; coyoteTimer: number; popBufferTimer: number
}

export function makeSkierSnapshot(): SkierSnapshot {
  return {
    x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, heading: 0, edge: 0,
    airborne: false, crashTimer: 0, sketchTimer: 0, popCooldown: 0, airTime: 0,
    crashes: 0, distance: 0, verticalDropped: 0,
    sluffLoad: 0, breakThroughTimer: 0, prevPop: false,
    gripUsed: 0, skid: 0,
    autoYaw: 0, coyoteTimer: 0, popBufferTimer: 0,
  }
}

/** Interpolatable pose, snapshotted before each tick for smooth rendering. */
export interface Pose {
  x: number
  y: number
  z: number
  heading: number
  roll: number
  pitch: number
}

export class Skier {
  // -- Integrated state ----------------------------------------------------
  x = 0
  y = 0
  z = 0
  vx = 0
  vy = 0
  vz = 0
  /** Yaw. Forward is (sin h, 0, cos h); +Z is downhill. */
  heading = 0
  /** Smoothed edge angle, -1..1. Positive = on the right edge, turning right. */
  edge = 0

  airborne = false
  crashTimer = 0
  sketchTimer = 0
  popCooldown = 0
  airTime = 0

  // -- Telemetry (read by the HUD and the camera; never fed back in) --------
  speed = 0
  slopeDeg = 0
  /** Fraction of available grip being used. > 1 means skidding. */
  gripUsed = 0
  /** Lateral (across-ski) speed, m/s. */
  skid = 0
  lastAirTime = 0
  lastLandSeverity = 0
  crashes = 0
  crashReason: CrashReason = 'none'
  /** Tally by reason, so a whole run can be read at a glance. */
  readonly crashLog: Record<CrashReason, number> = {
    none: 0,
    spinout: 0,
    'flat landing': 0,
    'sideways landing': 0,
    impact: 0,
    'broke through': 0,
    sluffed: 0,
  }
  distance = 0
  verticalDropped = 0
  startY = 0
  /** Terrain surface height directly under the skier. */
  groundY = 0
  /** Ground normal under the skier. */
  nx = 0
  ny = 1
  nz = 0
  readonly snow: SnowSample = makeSnowSample()

  /** Set by the sluff model when the skier is caught in moving snow (spec §6). */
  sluffLoad = 0
  /** Seconds since the last crust breakthrough, for feedback and the HUD. */
  breakThroughTimer = 0

  private readonly snowW = new Float32Array(SNOW_TYPE_COUNT)

  // -- Render interpolation ------------------------------------------------
  readonly prev: Pose = { x: 0, y: 0, z: 0, heading: 0, roll: 0, pitch: 0 }
  readonly curr: Pose = { x: 0, y: 0, z: 0, heading: 0, roll: 0, pitch: 0 }

  /** Deterministic, spatially coherent grip lottery for wind slab. */
  private readonly gripNoise = new Noise2D(mulberry32(0x9e3779b9))
  private readonly n = { x: 0, y: 1, z: 0 }
  private prevPop = false

  // -- Assist state (snapshotted — see SkierSnapshot) ------------------------
  /** Damped fall-line auto-centre correction, rad/s. */
  private autoYaw = 0
  /** Seconds after leaving the ground during which a pop still fires. */
  private coyoteTimer = 0
  /** Seconds a mistimed pop stays buffered, firing on the next touchdown. */
  private popBufferTimer = 0

  constructor(
    private readonly hf: Heightfield,
    private readonly obstacles: ObstacleField,
  ) {}

  get state(): RideState {
    if (this.crashTimer > 0) return 'crash'
    if (this.airborne) return 'air'
    if (this.sketchTimer > 0) return 'sketch'
    return 'ride'
  }

  reset(x: number, z: number, heading = 0): void {
    this.x = x
    this.z = z
    this.y = this.hf.sampleHeight(x, z)
    this.vx = 0
    this.vy = 0
    this.vz = 0
    this.heading = heading
    this.edge = 0
    this.airborne = false
    this.crashTimer = 0
    this.sketchTimer = 0
    this.popCooldown = 0
    this.airTime = 0
    this.sluffLoad = 0
    this.breakThroughTimer = 0
    this.lastAirTime = 0
    this.lastLandSeverity = 0
    this.crashes = 0
    this.crashReason = 'none'
    for (const k of Object.keys(this.crashLog) as CrashReason[]) this.crashLog[k] = 0
    this.distance = 0
    this.verticalDropped = 0
    this.startY = this.y
    this.speed = 0
    this.gripUsed = 0
    this.skid = 0
    this.prevPop = false
    this.autoYaw = 0
    this.coyoteTimer = 0
    this.popBufferTimer = 0
    this.syncPose()
    Object.assign(this.prev, this.curr)
  }

  /**
   * Full integrated state, for the solver's beam search to branch on and for
   * Phase 4's replay. Only the fields that actually feed the next step —
   * telemetry is recomputed.
   */
  save(out: SkierSnapshot): SkierSnapshot {
    out.x = this.x; out.y = this.y; out.z = this.z
    out.vx = this.vx; out.vy = this.vy; out.vz = this.vz
    out.heading = this.heading; out.edge = this.edge
    out.airborne = this.airborne
    out.crashTimer = this.crashTimer
    out.sketchTimer = this.sketchTimer
    out.popCooldown = this.popCooldown
    out.airTime = this.airTime
    out.crashes = this.crashes
    out.distance = this.distance
    out.verticalDropped = this.verticalDropped
    out.sluffLoad = this.sluffLoad
    out.breakThroughTimer = this.breakThroughTimer
    out.prevPop = this.prevPop
    out.gripUsed = this.gripUsed
    out.skid = this.skid
    out.autoYaw = this.autoYaw
    out.coyoteTimer = this.coyoteTimer
    out.popBufferTimer = this.popBufferTimer
    return out
  }

  load(s: SkierSnapshot): void {
    this.x = s.x; this.y = s.y; this.z = s.z
    this.vx = s.vx; this.vy = s.vy; this.vz = s.vz
    this.heading = s.heading; this.edge = s.edge
    this.airborne = s.airborne
    this.crashTimer = s.crashTimer
    this.sketchTimer = s.sketchTimer
    this.popCooldown = s.popCooldown
    this.airTime = s.airTime
    this.crashes = s.crashes
    this.distance = s.distance
    this.verticalDropped = s.verticalDropped
    this.sluffLoad = s.sluffLoad
    this.breakThroughTimer = s.breakThroughTimer
    this.prevPop = s.prevPop
    this.gripUsed = s.gripUsed
    this.skid = s.skid
    this.autoYaw = s.autoYaw
    this.coyoteTimer = s.coyoteTimer
    this.popBufferTimer = s.popBufferTimer
    this.speed = Math.hypot(this.vx, this.vy, this.vz)
  }

  /** Copy the current tick's pose into `prev` — call before stepping. */
  savePrevPose(): void {
    this.prev.x = this.curr.x
    this.prev.y = this.curr.y
    this.prev.z = this.curr.z
    this.prev.heading = this.curr.heading
    this.prev.roll = this.curr.roll
    this.prev.pitch = this.curr.pitch
  }

  private syncPose(): void {
    this.curr.x = this.x
    this.curr.y = this.y
    this.curr.z = this.z
    this.curr.heading = this.heading
    // Body roll follows the edge, damped by how much grip is actually loaded —
    // you cannot lay it over on a surface that isn't holding you.
    const load = this.airborne ? 0.35 : clamp01(1.15 - this.gripUsed * 0.55)
    this.curr.roll = this.edge * 0.62 * load
    // Pitch reads the slope: fore-aft lean, so the rider looks planted.
    this.curr.pitch = this.airborne ? clamp(this.vy * 0.02, -0.35, 0.35) : -this.slopeDeg * 0.006
  }

  step(input: InputFrame, dt: number): void {
    const prevX = this.x
    const prevY = this.y
    const prevZ = this.z

    if (this.popCooldown > 0) this.popCooldown -= dt
    if (this.sketchTimer > 0) this.sketchTimer -= dt
    if (this.coyoteTimer > 0) this.coyoteTimer -= dt
    if (this.popBufferTimer > 0) this.popBufferTimer -= dt

    // Sample the surface we are over, once per tick.
    this.hf.sampleNormal(this.x, this.z, this.n)
    this.nx = this.n.x
    this.ny = this.n.y
    this.nz = this.n.z
    this.groundY = this.hf.sampleHeight(this.x, this.z)
    this.slopeDeg = Math.acos(clamp(this.n.y, -1, 1)) * RAD
    this.hf.sampleSnowWeights(this.x, this.z, this.snowW)
    blendSnow(this.snowW, this.hf.sampleRock(this.x, this.z), this.snow)
    if (this.breakThroughTimer > 0) this.breakThroughTimer -= dt

    if (this.crashTimer > 0) {
      this.stepCrashed(dt)
    } else if (this.airborne) {
      this.stepAirborne(input, dt)
    } else {
      this.stepGrounded(input, dt)
    }

    this.speed = Math.hypot(this.vx, this.vy, this.vz)

    // Safety net: never let the skier end a tick under the surface.
    const h = this.hf.sampleHeight(this.x, this.z)
    if (this.y < h) {
      this.y = h
      if (this.vy < 0) this.vy = 0
    }
    // And never let them leave the field.
    this.x = clamp(this.x, this.hf.minX + 2, this.hf.maxX - 2)
    this.z = clamp(this.z, this.hf.minZ, this.hf.maxZ - 2)

    this.distance += Math.hypot(this.x - prevX, this.y - prevY, this.z - prevZ)
    if (prevY > this.y) this.verticalDropped += prevY - this.y
    void prevZ

    this.syncPose()
  }

  // -- Grounded ------------------------------------------------------------

  private stepGrounded(input: InputFrame, dt: number): void {
    const n = this.n
    const G = TUNE.GRAVITY
    const speed = Math.hypot(this.vx, this.vy, this.vz)

    // --- Steering: the ski goes where you point it -------------------------
    //
    // The old model steered the heading kinematically and then chased the
    // resulting lateral velocity with a clamped spring. Once the clamp bit,
    // the error integrated without bound — the HUD read "grip used 1836%",
    // a permanent skid tax ran on most of every run, and enough accumulated
    // sideslip was an instant spinout crash. Fun lost to bookkeeping.
    //
    // Now the velocity *chases the skis* by rotation-toward-alignment, which
    // is bounded by construction: drift converges exponentially, nothing
    // integrates, nothing spirals, and grip differences between snows show up
    // as how floaty that convergence is rather than as a failure cliff.
    let steerScale = 1
    if (input.tuck) steerScale *= TUNE.TUCK_STEER_SCALE
    if (input.brake) steerScale *= TUNE.BRAKE_STEER_SCALE

    this.edge = damp(this.edge, clamp(input.steer, -1, 1), TUNE.EDGE_RESPONSE, dt)

    // Carving: the edge sets a radius, speed sets the rate, grip caps it.
    //
    // rate = v / R while v²/R stays inside the grip budget, so at speed a
    // committed edge turns HARDER, not softer — which is both how skis work
    // and what dodging a tree at 25 m/s actually needs. Where demand crosses
    // the grip cap the carve saturates; the shortfall becomes drift via the
    // velocity-chases-skis alignment below, so overdriving a turn washes wide
    // instead of snapping. The old authority curve did the opposite (radius
    // grew with speed, 44m at 20 m/s, measured) and is the single reason the
    // controls felt bad the moment the forest became the game.
    const rMin = lerp(TUNE.CARVE_RADIUS_LOW, TUNE.CARVE_RADIUS_HIGH,
      clamp01(speed / TUNE.CARVE_RADIUS_SPEED))
    const carveRate = Math.min(speed / rMin, TUNE.CARVE_MAX_LAT_ACCEL / Math.max(speed, 0.5))
    // At a walk you can still pivot the skis — without this, stopping against
    // a tree soft-locked the run.
    const pivotRate = TUNE.PIVOT_TURN_RATE * clamp01(1 - speed / TUNE.PIVOT_FADE_SPEED)
    // Rock gives you nothing to turn against.
    const surfaceAuthority = lerp(1, 0.45, this.snow.rock)
    const fullEdgeRate = Math.max(carveRate, pivotRate) * surfaceAuthority
    this.heading -= this.edge * steerScale * fullEdgeRate * dt

    // Fall-line auto-centre: with the thumb released, drift gently toward
    // pointing down the hill. The corridors are carved into the heightfield,
    // so the local fall line already points down the course — no racing line,
    // no new data. Capped low and damped, so it can trim a line but never ski
    // one: par versus a zero-input run must stay minutes apart.
    const nh = Math.hypot(n.x, n.z)
    let assistTarget = 0
    if (
      Math.abs(input.steer) < 0.03 && !input.brake &&
      speed > 3 && nh > TUNE.AUTO_CENTER_MIN_SLOPE
    ) {
      // Downhill, horizontally, is (n.x, n.z)/nh — same frame as heading, so
      // with hd the fall-line heading, sin(hd−h) and cos(hd−h) expand to the
      // dot/cross below. Deliberately NOT Math.atan2: adding it to the hot
      // path made the on-device par solve (Chrome's V8) disagree with the CI
      // par solve (Node's V8) by four seconds — the beam search amplifies a
      // last-bit transcendental difference into different action choices. The
      // sin/cos already in this path are the ones both engines agreed on for
      // the whole life of the project; keep the set closed.
      const sh = Math.sin(this.heading)
      const ch = Math.cos(this.heading)
      const sinErr = (n.x * ch - n.z * sh) / nh
      const cosErr = (n.x * sh + n.z * ch) / nh
      // Facing uphill, sin alone is ambiguous — commit to the nearer way round.
      const err = cosErr > 0 ? sinErr : (sinErr >= 0 ? 1 : -1)
      assistTarget = clamp(
        err * TUNE.AUTO_CENTER_GAIN,
        -TUNE.AUTO_CENTER_MAX, TUNE.AUTO_CENTER_MAX,
      )
    }
    // Low-passed: heightfield normals are piecewise on grid cells, so the raw
    // target steps every few metres at speed.
    this.autoYaw = damp(this.autoYaw, assistTarget, TUNE.AUTO_CENTER_LAG, dt)
    this.heading += this.autoYaw * dt

    // --- Frame from the *updated* heading ----------------------------------
    let fx = Math.sin(this.heading)
    let fy = 0
    let fz = Math.cos(this.heading)
    const fdn = fx * n.x + fy * n.y + fz * n.z
    fx -= n.x * fdn
    fy -= n.y * fdn
    fz -= n.z * fdn
    const flen = Math.hypot(fx, fy, fz) || 1
    fx /= flen; fy /= flen; fz /= flen

    // Right axis = forward x normal (right-handed, Y-up, +Z downhill).
    const rx = fy * n.z - fz * n.y
    const ry = fz * n.x - fx * n.z
    const rz = fx * n.y - fy * n.x

    let vF = this.vx * fx + this.vy * fy + this.vz * fz
    let vL = this.vx * rx + this.vy * ry + this.vz * rz
    const vN = this.vx * n.x + this.vy * n.y + this.vz * n.z

    // --- Alignment: how fast the velocity comes around to the skis ---------
    // Snow character lives here. Powder converges fast (grippy, surfy), ice
    // slowly (floaty, drifty) — but drifting is a *state* now, not a countdown
    // to a crash. The wind-slab grip lottery and sluff underfoot both read as
    // moments of looseness rather than as force ceilings.
    const lottery =
      1 +
      this.snow.gripVariance *
        this.gripNoise.sample(this.x * TUNE.GRIP_NOISE_SCALE, this.z * TUNE.GRIP_NOISE_SCALE)
    const sluffGrip = 1 - clamp01(this.sluffLoad) * 0.55
    const edgeHold = TUNE.ALIGN_EDGE_FLOOR + (1 - TUNE.ALIGN_EDGE_FLOOR) * Math.abs(this.edge)
    const gripFactor = 0.45 + 0.55 * clamp01(this.snow.grip / 1.2)
    let alignRate = TUNE.ALIGN_BASE * gripFactor * edgeHold * lottery * sluffGrip
    if (input.brake) alignRate *= TUNE.ALIGN_BRAKE_BOOST

    const k = 1 - Math.exp(-Math.max(0, alignRate) * dt)
    const vT = Math.hypot(vF, vL)
    const removed = vL * k
    vL -= removed
    // Pure rotation: carving conserves speed *exactly*. The first version
    // returned only 55% of dissipated drift as forward speed, which meant
    // every heading change — the player's, the assist's per-cell corrections,
    // terrain jitter — leaked energy. At 25 m/s that leak ran to ~5 m/s²,
    // comparable to gravity on the whole pitch, and read as "it just slows
    // down for no reason when going downhill". Turning is free now; the costs
    // below are explicit and legible.
    const fSign = vF >= 0 ? 1 : -1
    vF = fSign * Math.sqrt(Math.max(0, vT * vT - vL * vL))
    // Carving never slows you down — that is the rule, stated absolutely.
    // Turning, drifting, slashing: all free. The check is the only thing that
    // scrubs (surface-priced: a braked slash in powder throws a wall of snow
    // and kills speed; on ice it is a long glassy drift). One gesture, one
    // meaning — if you are slowing down, it is because you asked to.
    const tax = input.brake
      ? clamp01(TUNE.BRAKE_SLIP_TAX + TUNE.SLIP_TAX * this.snow.skidDrag)
      : 0
    const scrub = tax * Math.abs(removed)
    const vT2 = Math.hypot(vF, vL)
    if (vT2 > 0.01) {
      const keepScale = Math.max(0, 1 - scrub / vT2)
      vF *= keepScale
      vL *= keepScale
    }

    this.skid = Math.abs(vL)
    // Bounded by construction. Feeds sluff (how hard the skis are cutting),
    // body roll, and spray — a 0..1 work signal, not a failure ratio.
    this.gripUsed = clamp01(
      this.skid / TUNE.GRIP_FULL_SKID + Math.abs(this.edge) * clamp01(speed / 12) * 0.4,
    )

    this.vx = fx * vF + rx * vL + n.x * vN
    this.vy = fy * vF + ry * vL + n.y * vN
    this.vz = fz * vF + rz * vL + n.z * vN

    // --- Accelerations ----------------------------------------------------
    // Gravity, tangential component.
    const gdn = -G * n.y
    let ax = -n.x * gdn
    let ay = -G - n.y * gdn
    let az = -n.z * gdn

    // Drag and plough resistance, opposing velocity.
    //
    // Plough falls away with speed: a fast ski planes up onto the snow instead
    // of pushing through it. This is real (dynamic lift goes with v²) and it is
    // the fun rule the surface game wants — carry speed into a powder patch
    // and you stay on top of it; arrive slow and it swallows you. Tucking also
    // trims plough now, not just air: on powder, plough is the *larger* force,
    // and a tuck that ignored it read as doing nothing downhill.
    const dragCoeff = this.snow.drag * (input.tuck ? TUNE.TUCK_DRAG_SCALE : 1)
    const dragMag = dragCoeff * speed
    const planing = 1 - TUNE.PLANE_REDUCE * clamp01(speed / TUNE.PLANE_SPEED)
    const resist =
      this.snow.resistance * planing * (input.tuck ? TUNE.TUCK_PLOUGH_SCALE : 1)
    ax -= this.vx * (dragMag + resist)
    ay -= this.vy * (dragMag + resist)
    az -= this.vz * (dragMag + resist)

    // --- Breakable crust ----------------------------------------------------
    // Spec §7: "Crust: fast until you break through." Where it holds it is the
    // fastest surface on the mountain; load it too hard and it grabs a ski.
    // Thin patches come from the same coherent noise field as the slab grip
    // lottery, so the surface is learnable across three runs rather than random.
    if (this.snow.breakable > 0.35 && this.breakThroughTimer <= 0 && speed > 8) {
      const thin = 0.5 + 0.5 * this.gripNoise.sample(this.x * 0.085 + 400, this.z * 0.085 - 250)
      const load = speed * (0.35 + Math.abs(this.edge) * 0.95) * this.snow.breakable
      if (load > lerp(19, 34, thin)) {
        this.breakThroughTimer = 0.9
        const keep = 0.62
        this.vx *= keep; this.vy *= keep; this.vz *= keep
        if (speed > 26) this.crash('broke through')
        else this.sketchTimer = TUNE.SKETCH_TIME
      }
    }

    // --- Sluff ---------------------------------------------------------------
    // Spec §6: being caught "scrubs speed and hits the control multiplier".
    // Getting buried is a crash, not a death.
    if (this.sluffLoad > 0.01) {
      const load = clamp01(this.sluffLoad)
      const push = load * TUNE.SLUFF_DRAG
      ax -= this.vx * push
      ay -= this.vy * push
      az -= this.vz * push
      if (load > TUNE.SLUFF_BURY && speed > 4) this.crash('sluffed')
      else if (load > TUNE.SLUFF_SKETCH) this.sketchTimer = TUNE.SKETCH_TIME
    }

    // Check / slarve: direct deceleration along the direction of travel.
    if (input.brake && speed > 0.4) {
      const b = TUNE.BRAKE_DECEL * clamp01(speed / 6)
      ax -= (this.vx / speed) * b
      ay -= (this.vy / speed) * b
      az -= (this.vz / speed) * b
    }

    this.vx += ax * dt
    this.vy += ay * dt
    this.vz += az * dt

    // --- Pop --------------------------------------------------------------
    // A pop flicked slightly early (while still airborne) lands in the buffer
    // and fires here, on the first grounded tick — mistimed inputs become
    // slightly-early inputs instead of eaten ones.
    const popEdge = input.pop && !this.prevPop
    this.prevPop = input.pop
    if ((popEdge || this.popBufferTimer > 0) && this.popCooldown <= 0) {
      this.popBufferTimer = 0
      this.vx += n.x * TUNE.POP_IMPULSE
      this.vy += n.y * TUNE.POP_IMPULSE
      this.vz += n.z * TUNE.POP_IMPULSE
      this.popCooldown = TUNE.POP_COOLDOWN
    }

    // --- Integrate and decide whether we left the ground -------------------
    const nextX = this.x + this.vx * dt
    const nextZ = this.z + this.vz * dt
    const hNext = this.hf.sampleHeight(nextX, nextZ)

    // If a free body would clear the ground at the next position, we launched.
    const yBallistic = this.y + this.vy * dt - 0.5 * TUNE.GRAVITY * dt * dt

    this.x = nextX
    this.z = nextZ

    if (yBallistic > hNext + TUNE.AIRBORNE_EPS) {
      this.airborne = true
      this.airTime = 0
      this.y = yBallistic
      // Coyote time: rolling off a lip a beat before the thumb catches up
      // should still give you the jump you meant to take.
      this.coyoteTimer = TUNE.COYOTE_TIME
    } else {
      this.y = hNext
      // Re-project velocity onto the new tangent plane and pay for whatever
      // normal velocity the surface just absorbed (a compression).
      this.hf.sampleNormal(nextX, nextZ, n)
      const vn = this.vx * n.x + this.vy * n.y + this.vz * n.z
      this.vx -= n.x * vn
      this.vy -= n.y * vn
      this.vz -= n.z * vn
      if (vn < 0) {
        const scrub = clamp01(-vn * TUNE.COMPRESSION_SCRUB * 0.1)
        this.vx *= 1 - scrub
        this.vy *= 1 - scrub
        this.vz *= 1 - scrub
      }
    }

    // Big drift reads as sketchy — spray, audio, the HUD state — but it is a
    // state you ride out, not a crash. Soft failure: sliding scrubs speed
    // (the alignment dissipation above already charges for it), and the clock
    // is the judge. The old spinout crash punished exactly the moments that
    // should feel the most alive.
    if (this.skid > TUNE.DRIFT_SKETCH && speed > 6) {
      this.sketchTimer = TUNE.SKETCH_TIME
    }

    this.collideObstacles()
  }

  // -- Airborne ------------------------------------------------------------

  private stepAirborne(input: InputFrame, dt: number): void {
    this.airTime += dt
    this.gripUsed = 0
    this.skid = 0

    // Weak air steering — enough to set up a landing, not enough to fly.
    this.edge = damp(this.edge, clamp(input.steer, -1, 1), TUNE.EDGE_RESPONSE * 0.7, dt)
    this.heading -= this.edge * TUNE.AIR_TURN_RATE * TUNE.AIR_STEER * dt

    const speed = Math.hypot(this.vx, this.vy, this.vz)
    const drag = TUNE.AIR_DRAG * speed
    this.vx -= this.vx * drag * dt
    this.vy -= this.vy * drag * dt
    this.vz -= this.vz * drag * dt
    this.vy -= TUNE.GRAVITY * dt

    // A pop just after rolling off a lip still fires (coyote time, along the
    // surface normal under us); one later than that is buffered and fires on
    // touchdown instead of being eaten.
    const popEdge = input.pop && !this.prevPop
    this.prevPop = input.pop
    if (popEdge) {
      if (this.coyoteTimer > 0 && this.popCooldown <= 0) {
        this.coyoteTimer = 0
        this.vx += this.n.x * TUNE.POP_IMPULSE
        this.vy += this.n.y * TUNE.POP_IMPULSE
        this.vz += this.n.z * TUNE.POP_IMPULSE
        this.popCooldown = TUNE.POP_COOLDOWN
      } else {
        this.popBufferTimer = TUNE.POP_BUFFER
      }
    }

    this.x += this.vx * dt
    this.y += this.vy * dt
    this.z += this.vz * dt

    const h = this.hf.sampleHeight(this.x, this.z)
    if (this.y <= h) {
      this.land(h)
    }
    this.collideObstacles()
  }

  /**
   * Landing. Spec §7: "Landing quality = angle between velocity and slope
   * normal. Matched landings on steep terrain preserve speed; flat landings
   * scrub it hard and can crash you."
   */
  private land(h: number): void {
    const n = this.n
    this.hf.sampleNormal(this.x, this.z, n)
    this.y = h

    const speed = Math.hypot(this.vx, this.vy, this.vz)
    const into = -(this.vx * n.x + this.vy * n.y + this.vz * n.z)

    this.lastAirTime = this.airTime
    this.airborne = false
    this.airTime = 0

    if (into <= 0 || speed < 0.001) {
      this.lastLandSeverity = 0
      return
    }

    // sin of the angle between the velocity and the slope plane: 0 for a
    // perfectly matched landing, 1 for straight into the deck.
    //
    // Gated by the absolute impact speed, because the ratio alone punishes
    // chatter: clipping a 20cm lump at 25 m/s can read as a 5% mismatch and
    // would otherwise cost 7% of your speed, several times a second. Knees and
    // ski flex absorb that for free, and the design only wants landing quality
    // to bite on real drops.
    const raw = clamp01(into / speed)
    const severity = raw * smoothstep(1.0, 3.0, into)
    this.lastLandSeverity = severity

    // Strip the normal component, then scale what's left.
    const vn = this.vx * n.x + this.vy * n.y + this.vz * n.z
    let tx = this.vx - n.x * vn
    let ty = this.vy - n.y * vn
    let tz = this.vz - n.z * vn

    const retain = clamp(1 - TUNE.LAND_SCRUB * severity, TUNE.LAND_MIN_RETAIN, 1)
    tx *= retain
    ty *= retain
    tz *= retain
    this.vx = tx
    this.vy = ty
    this.vz = tz

    // Landing sideways is its own problem: pull the heading toward the
    // direction of travel and treat the mismatch as extra impact.
    const flat = Math.hypot(tx, tz)
    if (flat > 1.5) {
      const travel = Math.atan2(tx, tz)
      const before = this.heading
      this.heading = lerp(this.heading, travel, 0.55)
      const mismatch = Math.abs(Math.atan2(Math.sin(before - travel), Math.cos(before - travel)))
      // 1.35 rad ≈ 77°: only a landing genuinely across the direction of
      // travel crashes. Anything less is absorbed as scrub + sketch — soft
      // failure suits a time trial better than a ragdoll.
      if (mismatch > 1.35 && speed > 9) this.crash('sideways landing')
    }

    if (into > TUNE.LAND_CRASH_IMPACT) {
      this.crash('flat landing')
    } else if (into > TUNE.LAND_SKETCH_IMPACT) {
      // Spec §4: a sketchy recovery is a real outcome, not a failure.
      this.sketchTimer = TUNE.SKETCH_TIME
    }
  }

  // -- Crashed -------------------------------------------------------------

  private stepCrashed(dt: number): void {
    this.crashTimer -= dt
    this.gripUsed = 0

    const n = this.n
    const G = TUNE.GRAVITY
    const gdn = -G * n.y
    let ax = -n.x * gdn
    let ay = -G - n.y * gdn
    let az = -n.z * gdn

    // A body sliding on its back: lots of friction, no steering.
    const speed = Math.hypot(this.vx, this.vy, this.vz)
    const friction = 2.6 + speed * 0.14
    if (speed > 0.01) {
      ax -= (this.vx / speed) * friction
      ay -= (this.vy / speed) * friction
      az -= (this.vz / speed) * friction
    }

    this.vx += ax * dt
    this.vy += ay * dt
    this.vz += az * dt

    this.x += this.vx * dt
    this.z += this.vz * dt
    this.y = this.hf.sampleHeight(this.x, this.z)

    // Re-project so we track the surface rather than burrowing.
    this.hf.sampleNormal(this.x, this.z, n)
    const vn = this.vx * n.x + this.vy * n.y + this.vz * n.z
    this.vx -= n.x * vn
    this.vy -= n.y * vn
    this.vz -= n.z * vn

    this.edge = damp(this.edge, 0, 6, dt)

    if (this.crashTimer <= 0) {
      // Back on your feet, pointed wherever you were sliding.
      const flat = Math.hypot(this.vx, this.vz)
      if (flat > 0.5) this.heading = Math.atan2(this.vx, this.vz)
      this.sketchTimer = TUNE.SKETCH_TIME
    }
  }

  private crash(reason: CrashReason): void {
    if (this.crashTimer > 0) return
    this.crashTimer = TUNE.CRASH_TIME
    this.airborne = false
    // A crash forfeits any queued assist.
    this.coyoteTimer = 0
    this.popBufferTimer = 0
    this.autoYaw = 0
    this.crashes++
    this.crashReason = reason
    this.crashLog[reason]++
    this.vx *= TUNE.CRASH_SPEED_KEEP
    this.vy *= TUNE.CRASH_SPEED_KEEP
    this.vz *= TUNE.CRASH_SPEED_KEEP
  }

  // -- Obstacles -----------------------------------------------------------

  private collideObstacles(): void {
    // Only things poking above the surface can be hit.
    const hit = this.obstacles.query(this.x, this.z, 0.85)
    if (!hit) return
    const surface = this.hf.sampleHeight(hit.x, hit.z)
    if (this.y > surface + hit.height) return

    const speed = Math.hypot(this.vx, this.vy, this.vz)

    // Push out along the contact normal.
    let dx = this.x - hit.x
    let dz = this.z - hit.z
    const d = Math.hypot(dx, dz) || 0.001
    dx /= d
    dz /= d
    const overlap = hit.radius + 0.85 - d
    if (overlap > 0) {
      this.x += dx * overlap
      this.z += dz * overlap
      this.y = Math.max(this.y, this.hf.sampleHeight(this.x, this.z))
    }

    // Remove the component heading into the obstacle. What survives is the
    // tangential slide past it.
    const vOut = this.vx * dx + this.vz * dz
    if (vOut < 0) {
      this.vx -= dx * vOut
      this.vz -= dz * vOut
    }

    // Cost scales with how square the hit was. A graze while carving past a
    // trunk should barely register; a head-on costs the run. This also stops a
    // skier who has come to rest against a tree from being welded to it —
    // their downhill slide is tangential, so it survives, and they slither
    // round rather than sitting there at 0 m/s forever.
    const headOn = speed > 0.01 ? clamp01(-vOut / speed) : 0
    const bite = hit.severity * headOn
    const keep = clamp01(1 - (1 - TUNE.IMPACT_SPEED_KEEP) * bite)
    this.vx *= keep
    this.vy *= keep
    this.vz *= keep

    if (speed * bite > TUNE.IMPACT_CRASH_SPEED) {
      this.crash('impact')
    } else if (bite > 0.2) {
      this.sketchTimer = TUNE.SKETCH_TIME
    }
  }
}
